import {
  Box,
  VStack,
  Button,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Image,
  HStack,
  Collapse,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { useUnsavedChangesStore } from '@/store/unsavedChanges'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { renderMenuIcon } from '@/lib/icons/menuIcons'
import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { TYPO_BODY } from '@/lib/typography'

interface NavigationItemDef {
  name: string
  href: string
  icon: string
}

interface SidebarProps {
  items?: NavigationItemDef[]
  isOpen?: boolean
  onClose?: () => void
  /** Desktop collapse state (md+) */
  isCollapsed?: boolean
}

const SIDEBAR_FONT_SIZE = TYPO_BODY
const SIDEBAR_COLLAPSED_WIDTH = '64px'
const SIDEBAR_EXPANDED_WIDTH = '272px'

const navButtonSx = {
  '> span': {
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
}

type SidebarLogoProps = {
  logoUrl: string
  appName: string
  isCollapsed?: boolean
  /** Retain support-admin background */
  bg: string
}

const SidebarLogo = memo(({ logoUrl, appName, isCollapsed, bg }: SidebarLogoProps) => {
  return (
    <Box
      pt={isCollapsed ? 2 : 4}
      px={isCollapsed ? 2 : 5}
      pb={isCollapsed ? 1 : 4}
      flexShrink={0}
      bg={bg}
    >
      <HStack
        spacing={isCollapsed ? 0 : 2}
        align="center"
        overflow="hidden"
        w="full"
        justifyContent={isCollapsed ? 'center' : 'flex-start'}
      >
        <Image
          key={logoUrl}
          src={logoUrl}
          alt="ALS Logo"
          height="32px"
          width={isCollapsed ? '32px' : 'auto'}
          objectFit="contain"
          loading="eager"
          flexShrink={0}
          onError={(e) => {
            const target = e.target as HTMLImageElement
            if (!isCollapsed && target.src !== '/assets/img/Actionlabs Horizontal logo 1.png') {
              target.src = '/assets/img/Actionlabs Horizontal logo 1.png'
            }
          }}
        />
        {!isCollapsed && appName ? (
          <Text
            fontSize="xs"
            textAlign="left"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            flex="1"
            minW={0}
          >
            | {appName}
          </Text>
        ) : null}
      </HStack>
    </Box>
  )
})
SidebarLogo.displayName = 'SidebarLogo'

const NavIcon = memo(({ icon }: { icon: string }) => (
  <Box display="inline-flex" alignItems="center" justifyContent="center" w="20px" h="20px" flexShrink={0}>
    {renderMenuIcon(icon)}
  </Box>
))
NavIcon.displayName = 'NavIcon'

const NavLabel = memo(({ label }: { label: string }) => (
  <Tooltip label={label} placement="right" hasArrow openDelay={400}>
    <Box minW={0} flex="1" overflow="hidden" textAlign="left">
      <Text noOfLines={1} textAlign="left" w="full">
        {label}
      </Text>
    </Box>
  </Tooltip>
))
NavLabel.displayName = 'NavLabel'

function SidebarComponent({
  items,
  isOpen = false,
  onClose,
  isCollapsed = false,
}: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const navigate = useNavigate()
  const requestLeave = useUnsavedChangesStore((s) => s.requestLeave)
  const { colorMode } = useColorMode()

  const guardedNavigate = useCallback(
    (path: string) => {
      requestLeave(() => {
        navigate(path)
        onClose?.()
      })
    },
    [navigate, onClose, requestLeave],
  )

  const userRoutes = useAuthStore((state) => state.user?.user_routes)
  const colors = useThemeColors()
  const { settings: siteSettings } = useSiteSettings()
  const borderColor = colors.border

  const sidebarBg = colors.sidebarBg
  const sidebarText = colors.sidebarText
  const sidebarHoverBg = colors.sidebarHoverBg
  const sidebarActiveBg = colors.sidebarActiveBg

  const collapsedW = SIDEBAR_COLLAPSED_WIDTH
  const expandedW = SIDEBAR_EXPANDED_WIDTH

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-open-groups')
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>
        return parsed
      }
    } catch {
      //
    }
    return {}
  })

  const manuallyClosedRef = useRef<Set<string>>(new Set())

  const sidenavLogo = siteSettings?.sidenav_logo

  const logoUrl = useMemo(() => {
    // Toggled (collapsed): use 32x32 icon — colored in light, white in dark
    if (isCollapsed) {
      const iconPath =
        colorMode === 'light'
          ? '/assets/img/als-icon-32x32.png'
          : '/assets/img/als-white-icon-32x32.png'
      return iconPath
    }
    const coloredLogo = '/assets/img/Actionlabs Horizontal logo.png'
    const whiteLogo = '/assets/img/als-white-logo.png'
    // Light mode: always use colored logo; dark mode: use site setting or white logo
    const baseUrl =
      colorMode === 'light' ? coloredLogo : (sidenavLogo || whiteLogo)
    const version = import.meta.env.DEV
      ? `?v=${baseUrl.split('/').pop()?.replace(/[^a-zA-Z0-9]/g, '') || 'dev'}`
      : `?v=${import.meta.env.VITE_BUILD_TIME || '1.0.0'}`
    return baseUrl.includes('?') ? baseUrl : `${baseUrl}${version}`
  }, [sidenavLogo, colorMode, isCollapsed])

  const appName = useMemo(
    () => siteSettings?.site_name || import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
    [siteSettings?.site_name],
  )

  const joinPaths = useCallback((parent: string, child: string) => {
    const a = (parent || '').replace(/\/+$/, '')
    const b = (child || '').replace(/^\/+/, '')
    const joined = `${a}/${b}`
    return joined.startsWith('/') ? joined : `/${joined}`
  }, [])

  // Backend may send child path as full path (e.g. /service-catalog/service-types) or relative (e.g. /service-types)
  const getChildPath = useCallback(
    (parentPath: string, childPath: string) => {
      const p = (childPath || '').trim()
      if (!p) return parentPath
      // Full path: starts with / and has more than one segment (e.g. /service-catalog/service-types)
      if (p.startsWith('/') && p.slice(1).includes('/')) {
        return p.replace(/\/+$/, '') || '/'
      }
      return joinPaths(parentPath, childPath)
    },
    [joinPaths],
  )

  const topLevelRoutes = useMemo(() => {
    const routes = Array.isArray(userRoutes) ? userRoutes : []
    const filtered = routes.filter((r: any) => {
      const hasDisplayableChildren =
        Array.isArray(r.children) && r.children.some((c: any) => c.side_nav === 'true')
      return r.side_nav === 'true' || hasDisplayableChildren
    })
    return filtered
  }, [userRoutes])

  const isActivePath = useCallback(
    (targetPath: string) => {
      const norm = (p: string) => ('/' + (p || '').replace(/^\/+/, '')).replace(/\/+$/, '')
      const cur = norm(pathname)
      const tgt = norm(targetPath)
      return cur === tgt || cur.startsWith(tgt + '/')
    },
    [pathname],
  )

  useEffect(() => {
    const groupsToOpen: Record<string, boolean> = {}
    topLevelRoutes.forEach((route: any) => {
      const groupKey = route.path || route.name
      if (manuallyClosedRef.current.has(groupKey)) return
      if (Array.isArray(route.children)) {
        const shouldOpen = route.children.some((c: any) =>
          isActivePath(getChildPath(route.path, c.path)),
        )
        if (shouldOpen || isActivePath(route.path)) groupsToOpen[groupKey] = true
      } else if (isActivePath(route.path)) {
        groupsToOpen[groupKey] = true
      }
    })

    const keysToOpen = Object.keys(groupsToOpen)
    const soleKey = keysToOpen.length > 0 ? keysToOpen[0] : null
    const next = soleKey ? { [soleKey]: true } : {}

    setOpenGroups((prev) => {
      const prevOpen = Object.keys(prev).find((k) => prev[k])
      if ((soleKey || null) === (prevOpen || null)) return prev
      return next
    })
  }, [pathname, topLevelRoutes, isActivePath, getChildPath])

  const hasActiveChild = useCallback(
    (route: any) => {
      if (!Array.isArray(route.children)) return false
      return route.children.some((c: any) => {
        if (c.side_nav !== 'true') return false
        const childPath = getChildPath(route.path, c.path)
        return isActivePath(childPath)
      })
    },
    [isActivePath, getChildPath],
  )

  const renderNavButtons = useCallback(
    (routes: any[]) =>
      routes.map((route: any) => {
        const displayableChildren = Array.isArray(route.children)
          ? route.children.filter((c: any) => c.side_nav === 'true')
          : []
        const hasDisplayableChildren = displayableChildren.length > 0

        const shouldShowChildren = !isCollapsed && hasDisplayableChildren

        // Standalone item
        if (route.side_nav === 'true' && !hasDisplayableChildren) {
          const isRouteActive = isActivePath(route.path)
          const btn = (
            <Button
              key={route.path}
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'flex-start'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH="36px"
              flexShrink={0}
              fontSize={SIDEBAR_FONT_SIZE}
              lineHeight="short"
              textAlign="left"
              sx={!isCollapsed ? navButtonSx : undefined}
              w={isCollapsed ? '100%' : 'full'}
              overflow="hidden"
              onClick={(e) => {
                e.preventDefault()
                guardedNavigate(route.path)
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isRouteActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isRouteActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack spacing={3} minW={0} w="full" overflow="hidden" justify="flex-start">
                  <NavIcon icon={route.icon} />
                  <NavLabel label={route.name} />
                </HStack>
              )}
            </Button>
          )
          return isCollapsed ? (
            <Tooltip key={route.path} label={route.name} placement="right" hasArrow>
              <Box w="100%" flexShrink={0}>
                {btn}
              </Box>
            </Tooltip>
          ) : (
            btn
          )
        }

        // Group with children
        if (hasDisplayableChildren) {
          const groupKey = route.path || route.name
          const groupIsOpen = !!openGroups[groupKey]
          const isParentActive = isActivePath(route.path) || hasActiveChild(route)

          const parentBtn = (
            <Button
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'space-between'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH="36px"
              flexShrink={0}
              fontSize={SIDEBAR_FONT_SIZE}
              lineHeight="short"
              textAlign="left"
              sx={!isCollapsed ? navButtonSx : undefined}
              w="100%"
              pr={isCollapsed ? 1.5 : 4}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (isCollapsed) {
                  if (route.path) guardedNavigate(route.path)
                  return
                }
                setOpenGroups((prev) => {
                  const newState = !prev[groupKey]
                  if (newState) {
                    manuallyClosedRef.current.delete(groupKey)
                    const updated = { [groupKey]: true }
                    try {
                      localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                    } catch {
                      //
                    }
                    return updated
                  }
                  manuallyClosedRef.current.add(groupKey)
                  const updated = { ...prev, [groupKey]: false }
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch {
                    //
                  }
                  return updated
                })
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isParentActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isParentActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack w="full" justify="space-between" spacing={2} minW={0} overflow="hidden">
                  <HStack spacing={3} minW={0} flex="1" overflow="hidden" justify="flex-start">
                    <NavIcon icon={route.icon} />
                    <NavLabel label={route.name} />
                  </HStack>
                  <Icon
                    as={groupIsOpen ? ChevronDown : ChevronRight}
                    boxSize={4}
                    flexShrink={0}
                  />
                </HStack>
              )}
            </Button>
          )

          return (
            <Box key={route.path} w="100%" flexShrink={0}>
              {isCollapsed ? (
                <Tooltip label={route.name} placement="right" hasArrow>
                  <Box w="100%">{parentBtn}</Box>
                </Tooltip>
              ) : (
                parentBtn
              )}
              {shouldShowChildren ? (
                <Collapse in={groupIsOpen} animateOpacity>
                  <VStack spacing={1} pl={6} align="stretch" mt={1} flexShrink={0}>
                    {displayableChildren.map((child: any) => {
                      const childPath = getChildPath(route.path, child.path)
                      const isChildActive = isActivePath(childPath)
                      return (
                        <Button
                          key={child.path}
                          variant="ghost"
                          justifyContent="flex-start"
                          alignItems="center"
                          w="full"
                          overflow="hidden"
                          px={3}
                          py={1.5}
                          minH="32px"
                          flexShrink={0}
                          fontSize={SIDEBAR_FONT_SIZE}
                          lineHeight="short"
                          textAlign="left"
                          sx={navButtonSx}
                          onClick={(e) => {
                            e.preventDefault()
                            guardedNavigate(childPath)
                          }}
                          color={sidebarText}
                          _hover={{ bg: sidebarHoverBg }}
                          bg={isChildActive ? sidebarActiveBg : 'transparent'}
                          fontWeight={isChildActive ? 'semibold' : 'normal'}
                        >
                          <HStack spacing={3} minW={0} w="full" overflow="hidden" justify="flex-start">
                            <NavIcon icon={child.icon} />
                            <NavLabel label={child.name} />
                          </HStack>
                        </Button>
                      )
                    })}
                  </VStack>
                </Collapse>
              ) : null}
            </Box>
          )
        }

        return null
      }),
    [
      openGroups,
      isActivePath,
      hasActiveChild,
      getChildPath,
      guardedNavigate,
      sidebarText,
      sidebarHoverBg,
      sidebarActiveBg,
      isCollapsed,
      onClose,
    ],
  )

  const fallbackItems = useMemo(
    () =>
      (items || []).map((it) => {
        const btn = (
          <Button
            key={it.href}
            variant="ghost"
            justifyContent={isCollapsed ? 'center' : 'flex-start'}
            alignItems="center"
            px={isCollapsed ? 1.5 : 4}
            py={isCollapsed ? 1.5 : 2}
            minH="36px"
            flexShrink={0}
            fontSize={SIDEBAR_FONT_SIZE}
            lineHeight="short"
            textAlign="left"
            sx={!isCollapsed ? navButtonSx : undefined}
            w={isCollapsed ? '100%' : 'full'}
            overflow="hidden"
            onClick={(e) => {
              e.preventDefault()
              guardedNavigate(it.href)
            }}
            color={sidebarText}
            _hover={{ bg: sidebarHoverBg }}
          >
            {isCollapsed ? (
              <NavIcon icon={it.icon} />
            ) : (
              <HStack spacing={3} minW={0} w="full" overflow="hidden" justify="flex-start">
                <NavIcon icon={it.icon} />
                <NavLabel label={it.name} />
              </HStack>
            )}
          </Button>
        )
        return isCollapsed ? (
          <Tooltip key={it.href} label={it.name} placement="right" hasArrow>
            <Box w="100%" flexShrink={0}>
              {btn}
            </Box>
          </Tooltip>
        ) : (
          btn
        )
      }),
    [items, guardedNavigate, sidebarText, sidebarHoverBg, isCollapsed],
  )

  const helpLinks = useMemo(
    () => [
      { name: 'User Manual', href: '/user-manual', icon: 'book-open' },
      { name: 'FAQ', href: '/faq', icon: 'help-circle' },
    ],
    [],
  )

  const renderHelpNav = useCallback(
    (collapsed: boolean) =>
      helpLinks.map((it) => {
        const isActive = isActivePath(it.href)
        const btn = (
          <Button
            key={it.href}
            variant="ghost"
            justifyContent={collapsed ? 'center' : 'flex-start'}
            alignItems="center"
            px={collapsed ? 1.5 : 4}
            py={collapsed ? 1.5 : 2}
            minH="36px"
            flexShrink={0}
            fontSize={SIDEBAR_FONT_SIZE}
            lineHeight="short"
            textAlign="left"
            sx={!collapsed ? navButtonSx : undefined}
            w={collapsed ? '100%' : 'full'}
            overflow="hidden"
            onClick={(e) => {
              e.preventDefault()
              guardedNavigate(it.href)
            }}
            color={sidebarText}
            _hover={{ bg: sidebarHoverBg }}
            bg={isActive ? sidebarActiveBg : 'transparent'}
            fontWeight={isActive ? 'semibold' : 'normal'}
          >
            {collapsed ? (
              <NavIcon icon={it.icon} />
            ) : (
              <HStack spacing={3} minW={0} w="full" overflow="hidden" justify="flex-start">
                <NavIcon icon={it.icon} />
                <NavLabel label={it.name} />
              </HStack>
            )}
          </Button>
        )
        return collapsed ? (
          <Tooltip key={it.href} label={it.name} placement="right" hasArrow>
            <Box w="100%" flexShrink={0}>
              {btn}
            </Box>
          </Tooltip>
        ) : (
          btn
        )
      }),
    [helpLinks, isActivePath, guardedNavigate, sidebarText, sidebarHoverBg, sidebarActiveBg],
  )

  return (
    <>
      {/* DESKTOP SIDEBAR (push layout) */}
      <Box
        w={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        minW={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        transition="width 0.2s ease"
        flexShrink={0}
        bg={sidebarBg}
        borderRight="1px"
        borderColor={borderColor}
        h="100vh"
        maxH="100vh"
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
        color={sidebarText}
        overflow="hidden"
      >
        <Box flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
          <SidebarLogo logoUrl={logoUrl} appName={appName ?? ''} isCollapsed={isCollapsed} bg={sidebarBg} />

          <VStack
            spacing={isCollapsed ? 1 : 2}
            align="stretch"
            px={isCollapsed ? 1 : 4}
            pt={isCollapsed ? 1 : 2}
            pb={isCollapsed ? 2 : 4}
            flex="1"
            minH={0}
            overflowY="auto"
            overflowX="hidden"
            sx={{
              // Prefer scrolling over compressing nav rows when the list overflows.
              '& > *': { flexShrink: 0 },
              scrollbarGutter: 'stable',
            }}
            css={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(148, 163, 184, 0.35)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(148, 163, 184, 0.6)',
              },
            }}
          >
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>

          {/* Pinned help links — always visible */}
          <VStack
            spacing={isCollapsed ? 1 : 2}
            align="stretch"
            flexShrink={0}
            px={isCollapsed ? 1 : 4}
            py={isCollapsed ? 2 : 3}
            borderTop="1px solid"
            borderColor={borderColor}
          >
            {renderHelpNav(!!isCollapsed)}
          </VStack>
        </Box>
      </Box>

      {/* MOBILE DRAWER (overlay) */}
      <Drawer isOpen={!!isOpen} placement="left" onClose={onClose || (() => {})}>
        <DrawerOverlay />
        <DrawerContent
          bg={sidebarBg}
          color={sidebarText}
          display="flex"
          flexDirection="column"
          h="100dvh"
          maxH="100dvh"
        >
          <DrawerCloseButton />
          <DrawerHeader flexShrink={0}>
            <HStack spacing={2} align="center" overflow="hidden" w="full">
              <Image src={logoUrl} alt="ALS Logo" height="24px" objectFit="contain" flexShrink={0} />
              {appName ? (
                <Text
                  fontSize="xs"
                  textAlign="left"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  flex="1"
                  minW={0}
                >
                  | {appName}
                </Text>
              ) : null}
            </HStack>
          </DrawerHeader>
          <DrawerBody display="flex" flexDirection="column" minH={0} p={4}>
            <VStack
              spacing={2}
              align="stretch"
              flex="1"
              overflowY="auto"
              sx={{ '& > *': { flexShrink: 0 } }}
            >
              {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
            </VStack>
            <VStack
              spacing={2}
              align="stretch"
              flexShrink={0}
              pt={3}
              mt={3}
              borderTop="1px solid"
              borderColor={borderColor}
            >
              {renderHelpNav(false)}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  let itemsEqual = prevProps.items === nextProps.items
  if (!itemsEqual && prevProps.items && nextProps.items) {
    const prevItems = prevProps.items
    const nextItems = nextProps.items
    itemsEqual =
      prevItems.length === nextItems.length &&
      prevItems.every((item, idx) => {
        const nextItem = nextItems[idx]
        return (
          nextItem &&
          item.href === nextItem.href &&
          item.name === nextItem.name &&
          item.icon === nextItem.icon
        )
      })
  }

  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.isCollapsed === nextProps.isCollapsed &&
    itemsEqual
  )
})

export default Sidebar